'use client'

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { format, parseISO } from 'date-fns'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { DashboardEvaluation } from './DashboardTable'
import { EvaluationMenu } from '../Evaluation/EvaluationMenu/EvaluationMenu'
import { EvaluationMenuIcon } from '../Evaluation/EvaluationMenu/Icons'
import { useEffect, useMemo } from 'react'
import {
  createCopyEvaluationLinkMenuItem,
  useCreateArchiveEvaluationMenuItem,
  useCreateDeleteEvaluationMenuItem,
  useCreateEvaluationVersionsMenuItem,
  useCreateRenameEvaluationMenuItem,
} from '../Evaluation/EvaluationMenu/menu-item-factory'
import { cn } from '~/utils/cn'
import {
  CreateEvaluationVersionMutateFn,
  archiveEvaluationMutationMap,
  createEvaluationVersionMutationMap,
  deleteEvaluationMutationMap,
  useChangeEvaluationStatusDashboardMutation,
  useCreateEvaluationVersionMutation,
  ChangeEvaluationStatusDashboardMutateFn,
} from '../Evaluation/EvaluationMenu/mutations'
import { useDashboardWorkspaceContext } from '~/app/(dashboard)/dashboardContext'
import { MemberAvatar, MemberAvatarGroup } from '../MemberAvatar'
import { canCollaboratorUpdateEvaluationStatus } from '../Evaluation/evaluation-permissions'
import { useGetCombinedCollaborators } from '../Evaluation/useEvaluationCollaborators'
import { EditEvaluationNameModal } from '../Evaluation/EditEvaluationNameModal/EditEvaluationNameModal'
import { isWorkspaceMemberViewer } from '~/app/(dashboard)/workspace/_utils'
import { useTranslation } from '~/i18n/useTranslation'

const formatDateTime = (dateStr: string) => {
  const dt = parseISO(`${dateStr}Z`)
  return format(dt, "MMM d 'at' h:mm a")
}

type DashboardEvaluationPresentable = DashboardEvaluation & {
  isArchiveMutationLoading?: boolean
  isDeleteMutationLoading?: boolean
  isCreateVersionMutationLoading?: boolean
  canArchive?: boolean
  canDelete?: boolean
}
type DashboardEvaluationTableProps = {
  evaluations: DashboardEvaluationPresentable[]
  ownership?: 'owned' | 'shared' | 'archived'
}

const hideOnMobileCols = ['collaborators', 'created', 'mod_date', 'owner']

const columnHelper = createColumnHelper<DashboardEvaluationPresentable>()
const useColumns = ({
  changeEvaluationStatusMutateFn,
  createEvaluationVersionMutateFn,
  isViewer,
}: {
  changeEvaluationStatusMutateFn: ChangeEvaluationStatusDashboardMutateFn
  createEvaluationVersionMutateFn: CreateEvaluationVersionMutateFn
  isViewer?: boolean
}) => {
  const { t } = useTranslation()
  const createRenameEvaluationMenuItem = useCreateRenameEvaluationMenuItem()
  const createArchiveEvaluationMenuItem = useCreateArchiveEvaluationMenuItem({
    mutate: changeEvaluationStatusMutateFn,
  })
  const createDeleteEvaluationMenuItem = useCreateDeleteEvaluationMenuItem({
    mutate: changeEvaluationStatusMutateFn,
  })

  const createEvaluationVersionsMenuItem = useCreateEvaluationVersionsMenuItem({
    mutate: createEvaluationVersionMutateFn,
  })

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row, {
        id: 'name',
        cell: (info) => {
          const { id, name } = info.getValue()
          return (
            <Link href={`/evaluations/${encodeURIComponent(id)}`}>{name}</Link>
          )
        },
        header: () => t('label.name'),
      }),
      columnHelper.accessor('createDate', {
        id: 'created',
        cell: (info) => formatDateTime(info.getValue()),
        header: () => t('label.createdAt'),
      }),
      columnHelper.accessor('modDate', {
        id: 'mod_date',
        cell: (info) => formatDateTime(info.getValue()),
        header: () => t('label.lastModified'),
      }),
      columnHelper.accessor((row) => row.owner, {
        id: 'owner',
        cell: (info) => {
          const infoData = info.getValue()
          return (
            <MemberAvatar
              name={infoData.profile?.fullName}
              email={infoData.profile?.email}
              avatar={infoData.profile?.avatar ?? '/logo192.png'}
              memberId={infoData.memberId}
              width={32}
              height={32}
              showTooltip
            />
          )
        },
        header: () => t('label.owner'),
      }),
      columnHelper.accessor((row) => row.collaborators, {
        id: 'collaborators',
        cell: (info) => (
          <MemberAvatarGroup
            members={info.getValue()}
            imageClassName="ring-2 ring-white"
            placeholderClassName="ring-2 ring-white"
            width={32}
            height={32}
            showTooltip
            maxItems={3}
          />
        ),
        header: () => t('label.collaborators'),
      }),
      columnHelper.accessor('id', {
        id: 'actions',
        cell: (info) => {
          // The parent evaluation id
          const evaluationId = info.row.original.evaluationVersions?.find(
            (ver) => ver.id === info.row.original.id
          )?.evaluationId
          return (
            <EvaluationMenu
              tooltipText="Archive, delete, etc."
              renderButtonContentClassName={(open) =>
                cn('hover:bg-[#EAEFF1] rounded-lg text-gray-500', {
                  'bg-[#EAEFF1]': open,
                })
              }
              menuContainerClassName="right-3/4 -mt-2"
              menuItems={[
                createRenameEvaluationMenuItem({
                  evaluationId: info.row.original.id,
                  evaluationVersion: info.row.original.versionNumber ?? -1,
                  evaluationName: info.row.original.name,
                  hidden: isViewer,
                }),
                createCopyEvaluationLinkMenuItem({
                  url: new URL(
                    `/evaluations/${encodeURIComponent(
                      info.getValue() as string
                    )}`,
                    window.location.origin
                  ).href,
                  t,
                }),
                ...(info.row.original.canArchive
                  ? [
                      createArchiveEvaluationMenuItem({
                        evaluationId,
                        evaluationVersionId: info.row.original.id,
                        status: info.row.original.status ?? [],
                        isLoading: info.row.original.isArchiveMutationLoading,
                        hidden: isViewer,
                      }),
                    ]
                  : []),
                ...(info.row.original.canDelete
                  ? [
                      createDeleteEvaluationMenuItem({
                        evaluationId,
                        evaluationVersionId: info.row.original.id,
                        evaluationName: info.row.original.name,
                        status: info.row.original.status ?? [],
                        isLoading: info.row.original.isDeleteMutationLoading,
                        hidden: isViewer,
                      }),
                    ]
                  : []),
                createEvaluationVersionsMenuItem({
                  versions: info.row.original.evaluationVersions ?? [],
                  currentVersionId: info.row.original.id,
                  subMenuContainerClassName: 'translate-y-[-105%]',
                  status: info.row.original.status ?? [],
                  createEvaluationVersionLoading:
                    info.row.original.isCreateVersionMutationLoading,
                  isCreateNewVersionHidden: isViewer,
                }),
              ]}
            >
              <EvaluationMenuIcon variant="vertical" className="w-6" />
            </EvaluationMenu>
          )
        },
        header: () => '',
      }),
    ],
    [createArchiveEvaluationMenuItem, createDeleteEvaluationMenuItem, isViewer, t]
  )
  return columns
}

export const DashboardEvaluationTable = ({
  ownership,
  evaluations,
}: DashboardEvaluationTableProps) => {
  const router = useRouter()
  const { t } = useTranslation()
  const {
    mutateAsync: changeEvaluationStatusMutateFn,
    isLoading: changeEvaluationStatusLoading,
  } = useChangeEvaluationStatusDashboardMutation()
  const {
    mutateAsync: createEvaluationVersionMutateFn,
    isLoading: createEvaluationVersionLoading,
  } = useCreateEvaluationVersionMutation()

  const dashboardWorkspaceContext = useDashboardWorkspaceContext()
  const { getCombinedCollaborators, refetchWorkspaceMembers, workspaceId } =
    useGetCombinedCollaborators()
  const member = dashboardWorkspaceContext.member

  const isViewer = isWorkspaceMemberViewer(member)

  useEffect(() => {
    if (workspaceId) {
      refetchWorkspaceMembers()
    }
  }, [workspaceId])

  const evaluationsPresentable = useMemo(
    (): DashboardEvaluationPresentable[] =>
      evaluations.map((evaluation) => {
        const { collaborator, collaborators } = getCombinedCollaborators(
          evaluation.collaborators
        )
        return {
          ...evaluation,
          isArchiveMutationLoading:
            changeEvaluationStatusLoading &&
            archiveEvaluationMutationMap.has(evaluation.id),
          isDeleteMutationLoading:
            changeEvaluationStatusLoading &&
            deleteEvaluationMutationMap.has(evaluation.id),
          isCreateVersionMutationLoading:
            createEvaluationVersionLoading &&
            createEvaluationVersionMutationMap.has(evaluation.id),
          canArchive: canCollaboratorUpdateEvaluationStatus(collaborator),
          canDelete: canCollaboratorUpdateEvaluationStatus(collaborator),
          collaborators,
        }
      }),
    [
      evaluations,
      changeEvaluationStatusLoading,
      createEvaluationVersionLoading,
      dashboardWorkspaceContext.workspace.id,
      getCombinedCollaborators,
    ]
  )

  const columns = useColumns({
    changeEvaluationStatusMutateFn,
    createEvaluationVersionMutateFn,
    isViewer,
  })

  const table = useReactTable<DashboardEvaluationPresentable>({
    data: evaluationsPresentable,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })
  const handleRowClick = (row: DashboardEvaluationPresentable) => () => {
    router.push(`/evaluations/${row.id}`)
  }

  if (table.getRowModel().rows.length === 0) {
    return (
      <div className="w-full flex flex-col gap-y-3 py-20 max-w-sm mx-auto items-center">
        {ownership === 'owned' && (
          <>
            <strong className="font-semibold text-gray-500 text-center text-sm">
              {t('dashboard.emptyOwned')}
            </strong>
            {!isViewer ? (
              <div>
                <Link
                  href="/evaluations/new"
                  className="bg-primary-500 text-sm font-semibold rounded px-3 py-2 text-white w-auto"
                >
                  {t('dashboard.createFirst')}
                </Link>
              </div>
            ) : null}
          </>
        )}
        {ownership === 'shared' && (
          <>
            <strong className="font-semibold text-gray-500 text-center text-sm">
              {t('dashboard.emptyShared')}
            </strong>
          </>
        )}
        {ownership === 'archived' && (
          <>
            <strong className="font-semibold text-gray-500 text-center text-sm">
              {t('dashboard.emptyArchived')}
            </strong>
          </>
        )}
      </div>
    )
  }
  return (
    <>
      <table className="w-full table-auto">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={`text-left font-normal first-of-type:pl-4 py-3 ${
                    hideOnMobileCols.includes(header.id)
                      ? 'hidden md:table-cell'
                      : ''
                  }`}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className="font-medium group cursor-pointer"
              onClick={handleRowClick(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className={`group-odd:bg-white first-of-type:rounded-l-lg first-of-type:pl-4 last-of-type:pr-4 last-of-type:rounded-r-lg py-3 ${
                    hideOnMobileCols.includes(
                      cell.id.replace(`${cell.row.id}_`, '')
                    )
                      ? 'hidden md:table-cell'
                      : ''
                  }`}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          {table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.footer,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>
      <EditEvaluationNameModal />
    </>
  )
}
